import { useEffect } from "react";

import type { RefObject } from "react";

const CLOSE_DUR = 75;
const OPEN_DUR = 195;
const EASE_IN_BLINK = "cubic-bezier(0.55,0,1,1)";
const EASE_OUT_BLINK = "cubic-bezier(0,0,0.25,1)";

// Idle blink: asymmetric (close fast / open slow), irregular gaps, occasional double blink.
export function useIdleBlink({
  enabled,
  eyesRef,
}: {
  enabled: boolean;
  eyesRef: RefObject<SVGElement | null>;
}) {
  useEffect(() => {
    if (!enabled) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const eyes = eyesRef.current;
    if (!eyes) return;

    const timers: number[] = [];
    const after = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const doBlink = (cb: () => void) => {
      eyes.style.transition = `transform ${CLOSE_DUR}ms ${EASE_IN_BLINK}`;
      eyes.style.transform = "scaleY(0.07)";
      after(CLOSE_DUR, () => {
        eyes.style.transition = `transform ${OPEN_DUR}ms ${EASE_OUT_BLINK}`;
        eyes.style.transform = "scaleY(1)";
        after(OPEN_DUR + 10, cb);
      });
    };

    const schedule = () => {
      const gap = 2400 + Math.random() * 4200;
      after(gap, () => {
        const isDouble = Math.random() < 0.22;
        doBlink(() => {
          if (isDouble) {
            after(110 + Math.random() * 100, () => doBlink(schedule));
          } else {
            schedule();
          }
        });
      });
    };

    schedule();

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      eyes.style.transition = "";
      eyes.style.transform = "";
    };
  }, [enabled, eyesRef]);
}
